/**
 * Field / section label with optional explanation as a native tooltip (info icon).
 * Keeps the form compact — no inline hint paragraphs under labels.
 */

import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

interface FieldLabelProps {
  children: React.ReactNode;
  /** Shown on hover via native title tooltip. */
  tooltip?: string;
  htmlFor?: string;
  /** Use for non-input headings (e.g. Rules, How it works). */
  as?: 'label' | 'span';
  className?: string;
}

export const FieldLabel: React.FC<FieldLabelProps> = ({
  children,
  tooltip,
  htmlFor,
  as = 'label',
  className = '',
}) => {
  const textClass = `text-base font-medium text-[var(--color-surface-text)] ${className}`.trim();

  return (
    <div className="mb-1 flex items-center gap-1.5">
      {as === 'span' ? (
        <span className={textClass}>{children}</span>
      ) : (
        <label htmlFor={htmlFor} className={textClass}>
          {children}
        </label>
      )}
      {tooltip ? (
        <span
          className="inline-flex shrink-0 text-[var(--color-surface-text-secondary)]"
          title={tooltip}
          aria-label={tooltip}
        >
          <InformationCircleIcon className="h-4 w-4" aria-hidden />
        </span>
      ) : null}
    </div>
  );
};
